import type { ValueBasis } from './ma-deal-case';
import { DECISION_AGENT_SOURCE_CLASSES } from './contracts/sourceClasses';

const PROMISE_BANDS = ['conservative', 'base', 'stretch'] as const;
const VALUE_BASES: readonly ValueBasis[] = ['run_rate', 'in_period', 'cumulative', 'one_time'];
const ECONOMIC_FIELDS = ['baselineByBand', 'targetByBand', 'retainedByBand'] as const;

type AllocationRecord = Record<string, unknown>;
type AllocationFailure = Readonly<{ ok: false; message: string }>;
type AllocationProblem = Readonly<{ ok: true }> | AllocationFailure;
type AllocationNodeIndex = Readonly<{
  nodes: readonly AllocationRecord[];
  byId: ReadonlyMap<string, AllocationRecord>;
}>;
type AllocationNodeIndexVerdict =
  | Readonly<{ ok: true; value: AllocationNodeIndex }>
  | AllocationFailure;
type AllocationHierarchyVerdict =
  | Readonly<{ ok: true; root: AllocationRecord }>
  | AllocationFailure;

/**
 * Admission for optional, versioned commitment partitions. A valid header is one
 * selected parent/scenario/version tree; this function never aggregates headers.
 */
export function maDealAllocationBreakdownsVerdict(
  allocationBreakdowns: unknown,
  promiseSets: readonly unknown[],
): AllocationProblem {
  if (allocationBreakdowns === undefined) return valid();
  if (!Array.isArray(allocationBreakdowns)) return invalid('allocationBreakdowns must be an array when supplied.');
  const commitments = commitmentsByRef(promiseSets);
  const identities = new Set<string>();
  for (const [index, header] of allocationBreakdowns.entries()) {
    const verdict = allocationHeaderVerdict(header, commitments);
    if (!verdict.ok) return invalid(`allocationBreakdowns[${index}] ${verdict.message}`);
    const identity = headerIdentity(header as AllocationRecord);
    if (identities.has(identity)) return invalid(`allocationBreakdowns contains duplicate parent/scenario/version identity ${identity}.`);
    identities.add(identity);
  }
  return valid();
}

function allocationHeaderVerdict(
  value: unknown,
  commitments: ReadonlyMap<string, AllocationRecord>,
): AllocationProblem {
  if (!isRecord(value)) return invalid('must be an object.');
  if (value.schemaVersion !== 'deal_commitment_allocation_breakdown_v1') return invalid('has an unsupported schemaVersion.');
  if (!isNonEmptyString(value.parentCommitmentRef)) return invalid('must declare parentCommitmentRef.');
  const parent = commitments.get(value.parentCommitmentRef);
  if (!parent) return invalid(`references unknown parent commitment ${value.parentCommitmentRef}.`);
  if (!isNonEmptyString(value.scenarioRef)) return invalid('must declare scenarioRef.');
  if (!isPositiveInteger(value.version)) return invalid('must declare a positive integer version.');
  if (!isSha256Digest(value.contentDigest)) return invalid('must declare a sha256 contentDigest.');
  if (!isValueBasis(value.valueBasis)) return invalid('must declare a governed valueBasis.');
  if (!isSourceClass(value.sourceClass)) return invalid('must declare a governed sourceClass.');
  return allocationTreeVerdict(value.nodes, parent, value.valueBasis);
}

function allocationTreeVerdict(
  value: unknown,
  parentCommitment: AllocationRecord,
  valueBasis: ValueBasis,
): AllocationProblem {
  const indexed = allocationNodeIndexVerdict(value, valueBasis);
  if (!indexed.ok) return indexed;
  const hierarchy = allocationHierarchyVerdict(indexed.value);
  if (!hierarchy.ok) return hierarchy;
  const root = rootCommitmentVerdict(hierarchy.root, parentCommitment);
  if (!root.ok) return root;
  return subtotalConservationVerdict(indexed.value);
}

function allocationNodeIndexVerdict(
  value: unknown,
  valueBasis: ValueBasis,
): AllocationNodeIndexVerdict {
  if (!Array.isArray(value) || value.length === 0) return invalid('nodes must be a non-empty array.');
  const byId = new Map<string, AllocationRecord>();
  const orders = new Set<number>();
  for (const [index, node] of value.entries()) {
    const nodeVerdict = allocationNodeVerdict(node, valueBasis);
    if (!nodeVerdict.ok) return invalid(`nodes[${index}] ${nodeVerdict.message}`);
    const record = node as AllocationRecord;
    const nodeId = record.nodeId as string;
    if (byId.has(nodeId)) return invalid(`contains duplicate nodeId ${nodeId}.`);
    if (orders.has(record.order as number)) return invalid(`contains duplicate order ${record.order}.`);
    byId.set(nodeId, record);
    orders.add(record.order as number);
  }
  return { ok: true, value: { nodes: value as readonly AllocationRecord[], byId } };
}

function allocationNodeVerdict(value: unknown, valueBasis: ValueBasis): AllocationProblem {
  if (!isRecord(value)) return invalid('must be an object.');
  if (!isNonEmptyString(value.nodeId)) return invalid('must declare nodeId.');
  if (value.parentNodeId !== null && !isNonEmptyString(value.parentNodeId)) return invalid('parentNodeId must be a string or null.');
  if (value.kind !== 'subtotal' && value.kind !== 'leaf') return invalid('must declare subtotal or leaf kind.');
  if (!isNonNegativeInteger(value.order)) return invalid('must declare a non-negative integer order.');
  if (!isNonEmptyString(value.label)) return invalid('must declare label.');
  if (value.kind === 'leaf' && !isNonEmptyString(value.inventoryRef)) return invalid('leaf nodes must declare inventoryRef.');
  if (value.kind === 'subtotal' && value.inventoryRef !== null) return invalid('subtotal nodes must use null inventoryRef.');
  if (value.disposition !== 'retire' && value.disposition !== 'retain') return invalid('must declare retire or retain disposition.');
  if (typeof value.protectedSystem !== 'boolean') return invalid('must declare protectedSystem.');
  if (!validTiming(value.timing)) return invalid('must declare typed timing references.');
  if (!isSourceClass(value.sourceClass)) return invalid('must declare a governed sourceClass.');
  return nodeEconomicsVerdict(value, valueBasis);
}

function nodeEconomicsVerdict(node: AllocationRecord, valueBasis: ValueBasis): AllocationProblem {
  for (const field of ECONOMIC_FIELDS) {
    const verdict = bandValuesVerdict(node[field], field, valueBasis);
    if (!verdict.ok) return verdict;
  }
  const costToAchieve = node.costToAchieve;
  if (!isRecord(costToAchieve) || !isNonNegativeFinite(costToAchieve.value) || !isValueBasis(costToAchieve.valueBasis)) {
    return invalid('costToAchieve must be a non-negative governed amount.');
  }
  const baseline = node.baselineByBand as AllocationRecord;
  const target = node.targetByBand as AllocationRecord;
  const retained = node.retainedByBand as AllocationRecord;
  for (const band of PROMISE_BANDS) {
    const baselineValue = (baseline[band] as AllocationRecord).value as number;
    const targetValue = (target[band] as AllocationRecord).value as number;
    const retainedValue = (retained[band] as AllocationRecord).value as number;
    if (!sameAmount(baselineValue, targetValue + retainedValue)) return invalid(`baselineByBand.${band} must equal targetByBand plus retainedByBand.`);
  }
  return valid();
}

function bandValuesVerdict(value: unknown, label: string, valueBasis: ValueBasis): AllocationProblem {
  if (!isRecord(value) || !hasExactlyPromiseBands(value)) return invalid(`${label} must declare exactly the governed promise bands.`);
  for (const band of PROMISE_BANDS) {
    const amount = value[band];
    if (!isRecord(amount) || !isNonNegativeFinite(amount.value) || amount.valueBasis !== valueBasis) {
      return invalid(`${label}.${band} must be a non-negative amount with the header valueBasis.`);
    }
  }
  return valid();
}

function allocationHierarchyVerdict(index: AllocationNodeIndex): AllocationHierarchyVerdict {
  const roots = index.nodes.filter((node) => node.parentNodeId === null);
  if (roots.length !== 1) return invalid('must declare exactly one root node.');
  const root = roots[0];
  if (!root || root.kind !== 'subtotal') return invalid('root node must be a subtotal.');
  const relationships = parentAndCycleVerdict(index);
  if (!relationships.ok) return relationships;
  const inventories = uniqueLeafInventoryVerdict(index.nodes);
  if (!inventories.ok) return inventories;
  return { ok: true, root };
}

function parentAndCycleVerdict(index: AllocationNodeIndex): AllocationProblem {
  for (const node of index.nodes) {
    const seen = new Set<string>();
    let current = node;
    while (current.parentNodeId !== null) {
      const currentId = current.nodeId as string;
      if (seen.has(currentId)) return invalid(`node ${node.nodeId} participates in a cycle.`);
      seen.add(currentId);
      const parentNodeId = current.parentNodeId as string;
      const parent = index.byId.get(parentNodeId);
      if (!parent) return invalid(`node ${node.nodeId} reaches unknown parentNodeId ${parentNodeId}.`);
      if (parent.kind !== 'subtotal') return invalid(`node ${node.nodeId} must attach to a subtotal parentNodeId.`);
      current = parent;
    }
  }
  return valid();
}
function uniqueLeafInventoryVerdict(nodes: readonly AllocationRecord[]): AllocationProblem {
  const inventoryRefs = new Set<string>();
  for (const node of nodes) {
    if (node.kind !== 'leaf') continue;
    const inventoryRef = node.inventoryRef as string;
    if (inventoryRefs.has(inventoryRef)) return invalid(`contains duplicate leaf inventoryRef ${inventoryRef}.`);
    inventoryRefs.add(inventoryRef);
  }
  return valid();
}

function rootCommitmentVerdict(root: AllocationRecord, commitment: AllocationRecord): AllocationProblem {
  if (!sameBandAmounts(root.targetByBand, commitment.targetByBand)) return invalid('root targetByBand must equal its parent commitment targetByBand.');
  if (!sameAmountRecord(root.costToAchieve, commitment.costToAchieve)) return invalid('root costToAchieve must equal its parent commitment costToAchieve.');
  return valid();
}

function subtotalConservationVerdict(index: AllocationNodeIndex): AllocationProblem {
  for (const subtotal of index.nodes.filter((node) => node.kind === 'subtotal')) {
    const children = index.nodes.filter((node) => node.parentNodeId === subtotal.nodeId);
    if (children.length === 0) return invalid(`subtotal ${subtotal.nodeId} must have direct children.`);
    for (const field of ECONOMIC_FIELDS) {
      const verdict = subtotalBandsVerdict(subtotal, children, field);
      if (!verdict.ok) return verdict;
    }
    const cost = subtotalCostVerdict(subtotal, children);
    if (!cost.ok) return cost;
  }
  return valid();
}

function subtotalBandsVerdict(
  subtotal: AllocationRecord,
  children: readonly AllocationRecord[],
  field: (typeof ECONOMIC_FIELDS)[number],
): AllocationProblem {
  const subtotalBands = subtotal[field] as AllocationRecord;
  for (const band of PROMISE_BANDS) {
    const subtotalAmount = subtotalBands[band] as AllocationRecord;
    const childAmounts = children.map((child) => (child[field] as AllocationRecord)[band] as AllocationRecord);
    if (childAmounts.some((amount) => amount.valueBasis !== subtotalAmount.valueBasis)) return invalid(`subtotal ${subtotal.nodeId} ${field}.${band} has a mixed valueBasis.`);
    const total = childAmounts.reduce((sum, amount) => sum + (amount.value as number), 0);
    if (!sameAmount(subtotalAmount.value as number, total)) return invalid(`subtotal ${subtotal.nodeId} ${field}.${band} must equal its direct children.`);
  }
  return valid();
}

function subtotalCostVerdict(subtotal: AllocationRecord, children: readonly AllocationRecord[]): AllocationProblem {
  const subtotalCost = subtotal.costToAchieve as AllocationRecord;
  const childCosts = children.map((child) => child.costToAchieve as AllocationRecord);
  if (childCosts.some((cost) => cost.valueBasis !== subtotalCost.valueBasis)) return invalid(`subtotal ${subtotal.nodeId} costToAchieve has a mixed valueBasis.`);
  const total = childCosts.reduce((sum, cost) => sum + (cost.value as number), 0);
  return sameAmount(subtotalCost.value as number, total)
    ? valid()
    : invalid(`subtotal ${subtotal.nodeId} costToAchieve must equal its direct children.`);
}

function commitmentsByRef(promiseSets: readonly unknown[]): ReadonlyMap<string, AllocationRecord> {
  const commitments = new Map<string, AllocationRecord>();
  for (const promiseSet of promiseSets) {
    if (!isRecord(promiseSet) || !Array.isArray(promiseSet.commitments)) continue;
    for (const commitment of promiseSet.commitments) {
      if (isRecord(commitment) && isNonEmptyString(commitment.commitmentRef)) commitments.set(commitment.commitmentRef, commitment);
    }
  }
  return commitments;
}

function headerIdentity(header: AllocationRecord): string {
  return JSON.stringify([header.parentCommitmentRef, header.scenarioRef, header.version]);
}

function sameBandAmounts(left: unknown, right: unknown): boolean {
  if (!isRecord(left) || !isRecord(right) || !hasExactlyPromiseBands(left) || !hasExactlyPromiseBands(right)) return false;
  return PROMISE_BANDS.every((band) => sameAmountRecord(left[band], right[band]));
}

function sameAmountRecord(left: unknown, right: unknown): boolean {
  return isRecord(left) && isRecord(right) && left.valueBasis === right.valueBasis && isFiniteNumber(left.value) && isFiniteNumber(right.value) && sameAmount(left.value, right.value);
}

function sameAmount(left: number, right: number): boolean {
  return Math.abs(left - right) <= Number.EPSILON * 16 * Math.max(1, Math.abs(left), Math.abs(right));
}

function hasExactlyPromiseBands(value: AllocationRecord): boolean {
  const keys = Object.keys(value);
  return keys.length === PROMISE_BANDS.length && PROMISE_BANDS.every((band) => Object.prototype.hasOwnProperty.call(value, band));
}

function validTiming(value: unknown): boolean {
  return isRecord(value) && isNullableRef(value.plannedStartDateRef) && isNullableRef(value.targetDateRef);
}

function isNullableRef(value: unknown): boolean {
  return value === null || isNonEmptyString(value);
}

function isSourceClass(value: unknown): boolean {
  return typeof value === 'string' && Object.values(DECISION_AGENT_SOURCE_CLASSES).includes(value as (typeof DECISION_AGENT_SOURCE_CLASSES)[keyof typeof DECISION_AGENT_SOURCE_CLASSES]);
}

function isSha256Digest(value: unknown): boolean {
  return typeof value === 'string' && /^sha256:[a-f0-9]{64}$/.test(value);
}

function isValueBasis(value: unknown): value is ValueBasis {
  return VALUE_BASES.includes(value as ValueBasis);
}

function isPositiveInteger(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function isNonNegativeInteger(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isNonNegativeFinite(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isRecord(value: unknown): value is AllocationRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function valid(): Readonly<{ ok: true }> {
  return { ok: true };
}

function invalid(message: string): AllocationFailure {
  return { ok: false, message };
}